/**
 * @fileoverview Editor component styles
 *
 * Contains styling for the MDXEditor wrapper and dark mode overrides.
 * Full-width editor that adjusts when panels are open.
 * Styling matches Writenex Editor design system.
 */

/* ============================================================================
   SEARCH HIGHLIGHT - CSS Highlight API for MDXEditor searchPlugin
   ============================================================================ */

::highlight(MdxSearch) {
  background-color: var(--wn-highlight-alpha-25);
}

::highlight(MdxFocusSearch) {
  background-color: var(--wn-highlight);
  color: #000000;
}

/* ============================================================================
   EDITOR CONTAINER
   ============================================================================ */

.wn-editor {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  height: 100%;
  background-color: var(--wn-zinc-900);
}

.wn-editor-content {
  flex: 1;
  overflow: auto;
  background-color: var(--wn-zinc-900);
  padding: var(--wn-space-3) var(--wn-space-5);
}

.wn-editor-wrapper {
  height: 100%;
}

/* ============================================================================
   MDXEDITOR DARK MODE OVERRIDES
   Matches Writenex Editor theme.css styling
   ============================================================================ */

/* Base MDXEditor variables */
.mdxeditor {
  /* Background colors */
  --baseBg: var(--wn-zinc-900);
  --baseBgSubtle: var(--wn-zinc-800);
  --baseBgHover: var(--wn-overlay-10);
  --baseBgActive: var(--wn-overlay-15);
  --basePageBg: var(--wn-zinc-950);

  /* Border colors */
  --baseBorder: var(--wn-zinc-700);
  --baseBorderSubtle: var(--wn-zinc-600);

  /* Text colors */
  --baseText: var(--wn-zinc-400);
  --baseTextContrast: var(--wn-zinc-300);
  --baseTextMuted: var(--wn-zinc-500);
  --baseTextSubtle: var(--wn-zinc-600);

  /* Accent colors */
  --accentColor: var(--wn-brand-400);
  --accentColorHover: var(--wn-brand-alpha-20);

  /* Selection */
  --selectionBg: var(--wn-selection);

  /* Font */
  --font-body:
    "Plus Jakarta Sans", system-ui, -apple-system, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
  --font-mono:
    ui-monospace, "Cascadia Code", Menlo, Consolas, "Courier New", monospace;
}

/* Editor root */
.mdxeditor,
.mdxeditor [class*="_editorRoot_"],
.mdxeditor [class*="_rootContentEditableWrapper_"] {
  background-color: var(--baseBg) !important;
}

/* Editor content */
.mdxeditor [data-lexical-editor] {
  color: var(--baseTextContrast) !important;
  font-family: var(--font-body) !important;
}

/* Content editable area - smaller padding like Writenex Editor */
.mdxeditor [class*="_contentEditable_"] {
  padding: 1rem 1.5rem !important;
  min-height: 100% !important;
}

/* ============================================================================
   PROSE TYPOGRAPHY - Matches Writenex Editor styling
   ============================================================================ */

/* Prose base styling */
.mdxeditor .prose {
  max-width: 75ch !important; /* optimal reading width ~65-75 characters */
  margin-left: auto !important;
  margin-right: auto !important;
  color: var(--wn-zinc-200) !important; /* body text */
  font-size: var(--wn-font-prose) !important; /* 15-16px fluid */
  line-height: 1.75 !important;
}

/* Headings - brighter color, proper spacing */
.mdxeditor .prose h1 {
  color: var(--wn-zinc-50) !important;
  font-size: var(--wn-font-h1) !important; /* 28-36px fluid */
  font-weight: 700 !important;
  line-height: 1.2 !important;
  margin-top: 0 !important;
  margin-bottom: 0.875rem !important;
}

.mdxeditor .prose h2 {
  color: var(--wn-zinc-50) !important;
  font-size: var(--wn-font-h2) !important; /* 20-24px fluid */
  font-weight: 600 !important;
  line-height: 1.3 !important;
  margin-top: 1.5rem !important;
  margin-bottom: 0.75rem !important;
}

.mdxeditor .prose h3 {
  color: var(--wn-zinc-50) !important;
  font-size: var(--wn-font-h3) !important; /* 18-20px fluid */
  font-weight: 600 !important;
  line-height: 1.4 !important;
  margin-top: 1.25rem !important;
  margin-bottom: 0.5rem !important;
}

.mdxeditor .prose h4 {
  color: var(--wn-zinc-50) !important;
  font-size: var(--wn-font-h4) !important; /* 16-18px fluid */
  font-weight: 600 !important;
  line-height: 1.4 !important;
  margin-top: 1rem !important;
  margin-bottom: 0.5rem !important;
}

.mdxeditor .prose h5 {
  color: var(--wn-zinc-50) !important;
  font-size: var(--wn-font-h5) !important; /* 15-16px, same as prose */
  font-weight: 600 !important;
  line-height: 1.4 !important;
  margin-top: 1rem !important;
  margin-bottom: 0.5rem !important;
}

.mdxeditor .prose h6 {
  color: var(
    --wn-zinc-300
  ) !important; /* slightly muted for visual hierarchy */
  font-size: var(--wn-font-h6) !important; /* 14px */
  font-weight: 600 !important;
  line-height: 1.4 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em !important;
  margin-top: 1rem !important;
  margin-bottom: 0.5rem !important;
}

/* Paragraphs */
.mdxeditor .prose p {
  color: var(--wn-zinc-200) !important;
  margin-top: 0 !important;
  margin-bottom: 1rem !important;
}

/* Strong/Bold text */
.mdxeditor .prose strong {
  color: var(--wn-zinc-50) !important;
  font-weight: 600 !important;
}

/* Emphasis/Italic text */
.mdxeditor .prose em {
  color: var(--wn-zinc-200) !important;
}

/* Lists */
.mdxeditor .prose ul,
.mdxeditor .prose ol {
  margin-top: 0.5rem !important;
  margin-bottom: 1rem !important;
  padding-left: 1.5rem !important;
}

.mdxeditor .prose li {
  color: var(--wn-zinc-200) !important;
  margin-top: 0.25rem !important;
  margin-bottom: 0.25rem !important;
}

.mdxeditor .prose ul > li::marker {
  color: var(--wn-zinc-500) !important;
}

.mdxeditor .prose ol > li::marker {
  color: var(--wn-zinc-400) !important;
}

/* Lead paragraph (first paragraph after heading) */
.mdxeditor .prose [class*="lead"] {
  color: var(--wn-zinc-400) !important;
}

/* Placeholder text */
.mdxeditor .editor-placeholder {
  color: var(--baseTextSubtle) !important;
}

/* ============================================================================
   TOOLBAR - Matches Writenex Editor styling (smaller padding)
   ============================================================================ */

.mdxeditor [class*="_toolbarRoot_"],
.mdxeditor [class*="_toolbar_"] {
  background-color: var(--baseBgSubtle) !important;
  border-color: var(--baseBorder) !important;
  border-bottom: 1px solid var(--baseBorder) !important;
  padding: var(--wn-space-1) var(--wn-space-3) !important;
  gap: 0.125rem !important;
}

/* Toolbar buttons - pointer cursor for all */
.mdxeditor [class*="_toolbarRoot_"] button,
.mdxeditor [class*="_toolbar_"] button,
.mdxeditor [class*="_selectTrigger_"],
.mdxeditor [class*="_toolbarModeSwitch_"] button,
.mdxeditor [class*="_codeMirrorToolbar_"] button,
[class*="_selectItem_"] {
  cursor: pointer !important;
}

.mdxeditor [class*="_toolbarRoot_"] button,
.mdxeditor [class*="_toolbar_"] button {
  color: var(--baseText) !important;
  border-radius: var(--wn-radius-sm) !important;
}

.mdxeditor [class*="_toolbarRoot_"] button:hover,
.mdxeditor [class*="_toolbar_"] button:hover {
  background-color: var(--baseBgHover) !important;
  color: var(--baseTextContrast) !important;
}

.mdxeditor [class*="_toolbarRoot_"] button[data-state="on"],
.mdxeditor [class*="_toolbar_"] button[data-state="on"] {
  background-color: var(--accentColorHover) !important;
  color: var(--accentColor) !important;
}

/* Toolbar select */
.mdxeditor [class*="_selectTrigger_"] {
  background-color: var(--baseBgHover) !important;
  border-color: var(--baseBorder) !important;
  color: var(--baseTextContrast) !important;
}

.mdxeditor [class*="_selectTrigger_"]:hover {
  background-color: var(--baseBgHover) !important;
  color: var(--baseTextContrast) !important;
}

/* Toolbar separator */
.mdxeditor [class*="_toolbarRoot_"] [class*="_separator_"],
.mdxeditor [class*="_toolbar_"] [class*="_separator_"] {
  background-color: var(--baseBorder) !important;
}

/* ============================================================================
   CODE BLOCKS & INLINE CODE
   ============================================================================ */

/* Code block wrapper */
.mdxeditor [class*="_codeMirrorWrapper_"] {
  border-color: var(--baseBorder) !important;
  background-color: var(--baseBg) !important;
  box-shadow: none !important;
}

/* Code block toolbar */
.mdxeditor [class*="_codeMirrorToolbar_"] {
  background-color: var(--baseBgSubtle) !important;
  border-bottom-color: var(--baseBorder) !important;
}

.mdxeditor [class*="_codeMirrorToolbar_"] button,
.mdxeditor [class*="_codeMirrorToolbar_"] [class*="_selectTrigger_"] {
  color: var(--baseText) !important;
}

.mdxeditor [class*="_codeMirrorToolbar_"] [class*="_selectTrigger_"]:hover {
  background-color: var(--baseBorder) !important;
  color: var(--baseTextContrast) !important;
}

/* Delete button hover (red) */
.mdxeditor [class*="_codeMirrorToolbar_"] button:last-child:hover {
  background-color: var(--wn-error-alpha-20) !important;
  color: var(--wn-error-400) !important;
}

/* CodeMirror editor */
.mdxeditor .cm-editor {
  background-color: var(--baseBg) !important;
}

.mdxeditor .cm-content {
  color: var(--baseTextContrast) !important;
  caret-color: var(--baseTextContrast) !important;
}

.mdxeditor .cm-gutters {
  background-color: var(--baseBgSubtle) !important;
  border-right: 1px solid var(--baseBorder) !important;
  color: var(--baseTextSubtle) !important;
}

.mdxeditor .cm-activeLineGutter {
  background-color: var(--baseBorder) !important;
  color: var(--baseTextContrast) !important;
}

.mdxeditor .cm-activeLine {
  background-color: var(--baseBgActive) !important;
}

.mdxeditor .cm-cursor {
  border-left-color: var(--baseTextContrast) !important;
}

.mdxeditor .cm-selectionBackground {
  background-color: var(--selectionBg) !important;
}

/* Inline code */
.mdxeditor code:not(pre code) {
  background-color: var(--baseBorder) !important;
  color: var(--baseTextContrast) !important;
  border: 1px solid var(--baseBorderSubtle) !important;
  border-radius: var(--wn-radius-sm) !important;
  padding: var(--wn-space-1) var(--wn-space-2) !important;
  font-family: var(--font-mono) !important;
}

.mdxeditor code:not(pre code) span {
  background-color: transparent !important;
  border: none !important;
  color: inherit !important;
}

/* ============================================================================
   DIALOGS
   ============================================================================ */

.mdxeditor [class*="_dialogContent_"] {
  background-color: var(--baseBg) !important;
  border: 1px solid var(--baseBorder) !important;
  color: var(--baseTextContrast) !important;
}

.mdxeditor [class*="_dialogContent_"] input,
.mdxeditor [class*="_dialogContent_"] textarea {
  background-color: var(--baseBgSubtle) !important;
  border: 1px solid var(--baseBorder) !important;
  color: var(--baseTextContrast) !important;
}

.mdxeditor [class*="_dialogContent_"] input:focus,
.mdxeditor [class*="_dialogContent_"] textarea:focus {
  border-color: var(--accentColor) !important;
  box-shadow: 0 0 0 2px var(--accentColorHover) !important;
  outline: none !important;
}

.mdxeditor [class*="_dialogContent_"] label {
  color: var(--baseText) !important;
}

/* ============================================================================
   CONTENT ELEMENTS
   ============================================================================ */

/* Images - constrain to editor width */
.mdxeditor img {
  max-width: 100% !important;
  height: auto !important;
  display: block !important;
  margin: var(--wn-space-5) 0 !important;
  border-radius: var(--wn-radius-md) !important;
}

/* Image wrapper from MDXEditor */
.mdxeditor [class*="_imageWrapper_"] {
  max-width: 100% !important;
}

/* Links */
.mdxeditor a {
  color: var(--accentColor) !important;
  text-decoration: underline !important;
}

/* Blockquote - matches Writenex Editor */
.mdxeditor blockquote {
  border-left: 4px solid var(--accentColor) !important;
  background-color: var(--wn-zinc-900) !important;
  padding: var(--wn-space-5) !important;
  margin: var(--wn-space-5) 0 !important;
  border-radius: 0 var(--wn-radius-md) var(--wn-radius-md) 0 !important;
  color: var(--wn-zinc-200) !important;
}

.mdxeditor blockquote p {
  margin: 0 !important;
}

/* Tables */
.mdxeditor table,
.mdxeditor th,
.mdxeditor td {
  border-color: var(--baseBorder) !important;
}

.mdxeditor th {
  background-color: var(--baseBgSubtle) !important;
}

/* ============================================================================
   CHECKLIST
   ============================================================================ */

.mdxeditor li[role="checkbox"]::before {
  background-color: var(--baseBgSubtle) !important;
  border-color: var(--baseBorderSubtle) !important;
}

.mdxeditor li[role="checkbox"][aria-checked="true"]::before {
  background-color: var(--accentColor) !important;
  border-color: var(--accentColor) !important;
}

.mdxeditor li[role="checkbox"][aria-checked="true"]::after {
  border-color: #ffffff !important;
}

.mdxeditor li[role="checkbox"]:hover::before {
  border-color: var(--accentColor) !important;
}

.mdxeditor li[role="checkbox"][aria-checked="true"] {
  color: var(--baseTextMuted) !important;
}

/* ============================================================================
   DROPDOWNS & POPOVERS (rendered via portal)
   ============================================================================ */

[class*="_selectContainer_"],
[class*="_toolbarNodeKindSelectContainer_"],
[class*="_toolbarButtonDropdownContainer_"] {
  background-color: var(--wn-zinc-800) !important;
  border: 1px solid var(--wn-zinc-700) !important;
  color: var(--wn-zinc-300) !important;
  box-shadow: 0 4px 6px -1px var(--wn-backdrop-light) !important;
  max-height: 300px !important;
  overflow-y: auto !important;
}

[class*="_selectItem_"] {
  color: var(--wn-zinc-300) !important;
  background-color: transparent !important;
}

[class*="_selectItem_"]:hover,
[class*="_selectItem_"][data-highlighted],
[class*="_selectItem_"][data-state="checked"] {
  background-color: var(--wn-zinc-700) !important;
  color: var(--wn-zinc-300) !important;
}

/* ============================================================================
   DIFF SOURCE TOGGLE
   ============================================================================ */

[class*="_diffSourceToggle_"],
[class*="_toolbarModeSwitch_"] {
  background-color: var(--wn-zinc-800) !important;
  border-color: var(--wn-zinc-700) !important;
  border-radius: var(--wn-radius-md) !important;
}

[class*="_diffSourceToggle_"] button,
[class*="_toolbarModeSwitch_"] button {
  color: var(--wn-zinc-400) !important;
  padding: var(--wn-space-1) var(--wn-space-3) !important;
  font-size: var(--wn-font-xs) !important; /* 12px */
}

[class*="_diffSourceToggle_"] button:hover,
[class*="_toolbarModeSwitch_"] button:hover {
  background-color: var(--wn-zinc-700) !important;
  color: var(--wn-zinc-300) !important;
}

[class*="_diffSourceToggle_"] button[data-state="on"],
[class*="_toolbarModeSwitch_"] button[data-state="on"] {
  background-color: var(--wn-zinc-700) !important;
  color: var(--wn-zinc-300) !important;
}

/* ============================================================================
   SOURCE/DIFF MODE
   ============================================================================ */

/* Source editor */
.mdxeditor [class*="_sourceEditor_"],
.mdxeditor [class*="_sourceEditor_"] .cm-editor {
  background-color: var(--baseBg) !important;
  color: var(--baseTextContrast) !important;
}

.mdxeditor [class*="_sourceEditor_"] .cm-scroller {
  background-color: var(--baseBg) !important;
}

.mdxeditor [class*="_sourceEditor_"] .cm-content {
  color: var(--baseTextContrast) !important;
  caret-color: var(--baseTextContrast) !important;
}

.mdxeditor [class*="_sourceEditor_"] .cm-content .cm-line {
  color: var(--baseTextContrast) !important;
}

.mdxeditor [class*="_sourceEditor_"] .cm-content .cm-line span {
  color: var(--baseTextContrast) !important;
}

.mdxeditor [class*="_sourceEditor_"] .cm-gutters {
  background-color: var(--baseBgSubtle) !important;
  border-right: 1px solid var(--baseBorder) !important;
  color: var(--baseTextMuted) !important;
}

.mdxeditor [class*="_sourceEditor_"] .cm-activeLineGutter {
  background-color: var(--baseBorder) !important;
}

.mdxeditor [class*="_sourceEditor_"] .cm-activeLine {
  background-color: var(--baseBgActive) !important;
}

/* Diff mode */
.mdxeditor [class*="_diffEditor_"],
.mdxeditor .cm-mergeView {
  background-color: var(--wn-zinc-950) !important;
}

.mdxeditor [class*="_diffEditor_"] .cm-editor,
.mdxeditor .cm-mergeView .cm-scroller {
  background-color: var(--wn-zinc-950) !important;
}

.mdxeditor [class*="_diffEditor_"] .cm-content {
  color: var(--baseTextContrast) !important;
}

.mdxeditor [class*="_diffEditor_"] .cm-gutters,
.mdxeditor .cm-mergeView .cm-gutters {
  background-color: var(--wn-zinc-900) !important;
  border-right: 1px solid var(--wn-zinc-700) !important;
  color: var(--wn-zinc-500) !important;
}

/* Diff highlighting */
.mdxeditor .cm-changedLine {
  background-color: var(--wn-success-alpha-15) !important;
}

.mdxeditor .cm-changedText {
  background-color: var(--wn-success-alpha-40) !important;
}

.mdxeditor .cm-deletedChunk {
  background-color: var(--wn-error-alpha-15) !important;
}

/* ============================================================================
   TOOLTIP
   ============================================================================ */

.mdxeditor [data-radix-popper-content-wrapper] [class*="tooltipContent"] {
  background-color: var(--wn-zinc-50) !important;
  color: var(--wn-zinc-900) !important;
}

/* ============================================================================
   EDITOR LOADING STATE
   ============================================================================ */

.wn-editor-loading {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--wn-space-4);
  color: var(--wn-zinc-500);
}

.wn-editor-loading-spinner {
  width: var(--wn-icon-btn-sm);
  height: var(--wn-icon-btn-sm);
  border: 2px solid var(--wn-zinc-700);
  border-top-color: var(--wn-brand-500);
  border-radius: var(--wn-radius-full);
  animation: wn-spin 1s linear infinite;
}

.wn-editor-loading-text {
  font-size: var(--wn-font-base); /* 14px */
}

@keyframes wn-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ============================================================================
   EDITOR EMPTY STATE
   ============================================================================ */

.wn-editor-empty {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--wn-space-5);
  padding: var(--wn-space-8);
  text-align: center;
}

.wn-editor-empty-icon {
  color: var(--wn-zinc-600);
}

.wn-editor-empty-title {
  font-size: var(--wn-font-lg); /* 18px */
  font-weight: 500;
  color: var(--wn-zinc-100);
}

.wn-editor-empty-text {
  max-width: var(--wn-modal-sm);
  font-size: var(--wn-font-base); /* 14px */
  color: var(--wn-zinc-500);
}

.wn-editor-empty-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--wn-space-3);
  margin-top: var(--wn-space-5);
  padding: var(--wn-space-3) var(--wn-space-6);
  background-color: var(--wn-brand-500);
  border: none;
  border-radius: var(--wn-radius-lg);
  font-size: var(--wn-font-base); /* 14px */
  font-weight: 500;
  color: #fff;
  cursor: pointer;
  transition: background-color var(--wn-transition-fast) ease;
}

.wn-editor-empty-btn:hover {
  background-color: var(--wn-brand-600);
}

.wn-editor-empty-btn:focus-visible {
  outline: 2px solid var(--wn-brand-500);
  outline-offset: 2px;
}

.wn-editor-empty-shortcuts {
  display: flex;
  flex-direction: column;
  gap: var(--wn-space-3);
  margin-top: var(--wn-space-7);
  padding-top: var(--wn-space-7);
  border-top: 1px solid var(--wn-zinc-700);
}

.wn-editor-empty-shortcut {
  display: flex;
  align-items: center;
  gap: var(--wn-space-2);
  font-size: var(--wn-font-xs); /* 12px */
  color: var(--wn-zinc-500);
}

.wn-editor-empty-shortcut kbd {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: var(--wn-icon-btn-sm);
  height: 1.375rem;
  padding: 0 var(--wn-space-2);
  background-color: var(--wn-zinc-800);
  border: 1px solid var(--wn-zinc-700);
  border-radius: var(--wn-radius-sm);
  font-family: inherit;
  font-size: var(--wn-font-xs); /* 12px - increased from 11px for readability */
  font-weight: 500;
  color: var(--wn-zinc-400);
}

/* ============================================================================
   LIGHT MODE OVERRIDES
   ============================================================================ */

.wn-light .wn-editor {
  background-color: #fff;
}

.wn-light .wn-editor-content {
  background-color: #fff;
}

.wn-light .wn-editor-loading {
  color: var(--wn-zinc-500);
}

.wn-light .wn-editor-loading-spinner {
  border-color: var(--wn-zinc-200);
  border-top-color: var(--wn-brand-500);
}

.wn-light .wn-editor-empty-icon {
  color: var(--wn-zinc-400);
}

.wn-light .wn-editor-empty-title {
  color: var(--wn-zinc-900);
}

.wn-light .wn-editor-empty-text {
  color: var(--wn-zinc-500);
}

.wn-light .wn-editor-empty-shortcuts {
  border-top-color: var(--wn-zinc-200);
}

.wn-light .wn-editor-empty-shortcut kbd {
  background-color: var(--wn-zinc-100);
  border-color: var(--wn-zinc-200);
  color: var(--wn-zinc-600);
}

/* MDXEditor Light Mode */
.wn-light .mdxeditor {
  --baseBg: #ffffff;
  --baseBgSubtle: var(--wn-zinc-50);
  --baseBgHover: var(--wn-overlay-light-5);
  --baseBgActive: var(--wn-overlay-light-10);
  --basePageBg: var(--wn-zinc-100);
  --baseBorder: var(--wn-zinc-200);
  --baseBorderSubtle: var(--wn-zinc-300);
  --baseText: var(--wn-zinc-500);
  --baseTextContrast: var(--wn-zinc-800);
  --baseTextMuted: var(--wn-zinc-400);
  --baseTextSubtle: var(--wn-zinc-300);
  --accentColor: var(--wn-brand-600);
  --accentColorHover: var(--wn-brand-alpha-10);
  --selectionBg: var(--wn-selection-light);
}

.wn-light .mdxeditor .prose {
  color: var(--wn-zinc-700) !important;
}

.wn-light .mdxeditor .prose h1,
.wn-light .mdxeditor .prose h2,
.wn-light .mdxeditor .prose h3,
.wn-light .mdxeditor .prose h4,
.wn-light .mdxeditor .prose h5 {
  color: var(--wn-zinc-900) !important;
}

.wn-light .mdxeditor .prose h6 {
  color: var(
    --wn-zinc-600
  ) !important; /* slightly muted for visual hierarchy in light mode */
}

.wn-light .mdxeditor .prose p,
.wn-light .mdxeditor .prose li,
.wn-light .mdxeditor .prose em {
  color: var(--wn-zinc-700) !important;
}

.wn-light .mdxeditor .prose strong {
  color: var(--wn-zinc-900) !important;
}

.wn-light .mdxeditor .prose ul > li::marker {
  color: var(--wn-zinc-400) !important;
}

.wn-light .mdxeditor .prose ol > li::marker {
  color: var(--wn-zinc-500) !important;
}

.wn-light .mdxeditor blockquote {
  background-color: var(--wn-zinc-100) !important;
  color: var(--wn-zinc-600) !important;
}

.wn-light .mdxeditor code:not(pre code) {
  background-color: var(--wn-zinc-100) !important;
  border-color: var(--wn-zinc-200) !important;
  color: var(--wn-zinc-900) !important;
}

/* Light mode dropdowns */
.wn-light [class*="_selectContainer_"],
.wn-light [class*="_toolbarNodeKindSelectContainer_"],
.wn-light [class*="_toolbarButtonDropdownContainer_"] {
  background-color: #fff !important;
  border-color: var(--wn-zinc-200) !important;
  color: var(--wn-zinc-800) !important;
  box-shadow: 0 4px 6px -1px var(--wn-overlay-light-10) !important;
}

.wn-light [class*="_selectItem_"] {
  color: var(--wn-zinc-800) !important;
}

.wn-light [class*="_selectItem_"]:hover,
.wn-light [class*="_selectItem_"][data-highlighted],
.wn-light [class*="_selectItem_"][data-state="checked"] {
  background-color: var(--wn-zinc-100) !important;
}

.wn-light [class*="_diffSourceToggle_"],
.wn-light [class*="_toolbarModeSwitch_"] {
  background-color: var(--wn-zinc-50) !important;
  border-color: var(--wn-zinc-200) !important;
}

.wn-light [class*="_diffSourceToggle_"] button,
.wn-light [class*="_toolbarModeSwitch_"] button {
  color: var(--wn-zinc-500) !important;
}

.wn-light [class*="_diffSourceToggle_"] button:hover,
.wn-light [class*="_toolbarModeSwitch_"] button:hover {
  background-color: var(--wn-zinc-200) !important;
  color: var(--wn-zinc-800) !important;
}

.wn-light [class*="_diffSourceToggle_"] button[data-state="on"],
.wn-light [class*="_toolbarModeSwitch_"] button[data-state="on"] {
  background-color: var(--wn-zinc-200) !important;
  color: var(--wn-zinc-800) !important;
}
